import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Gift, Loader2, Sparkles } from "lucide-react";
import { GiftPreviewMockup } from "./GiftPreviewMockup";

interface CreateGiftDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  generationId: string;
  trackTitle: string;
  audioUrl: string;
  audioUrl2?: string | null;
}

const EVENT_PRESETS = [
  "🎂 День рождения",
  "💕 Годовщина",
  "💍 Свадьба",
  "👩 8 Марта",
  "🎓 Выпускной",
  "🎉 Праздник",
];

export function CreateGiftDialog({
  open, onOpenChange, generationId, trackTitle, audioUrl, audioUrl2,
}: CreateGiftDialogProps) {
  const { toast } = useToast();
  const [heroName, setHeroName] = useState("");
  const [eventName, setEventName] = useState("");
  const [senderName, setSenderName] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [giftStyle, setGiftStyle] = useState<"light" | "black">("light");
  const [selectedVersion, setSelectedVersion] = useState(1);
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!heroName.trim()) {
      toast({ title: "Укажите имя получателя", variant: "destructive" });
      return;
    }
    if (!eventName.trim()) {
      toast({ title: "Укажите событие", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const response = await supabase.functions.invoke("web-create-gift", {
        body: {
          generation_id: generationId,
          hero_name: heroName.trim(),
          event_name: eventName.trim(),
          sender_name: senderName.trim() || null,
          custom_message: customMessage.trim() || null,
          gift_style: giftStyle,
          selected_track_version: selectedVersion,
        },
      });

      if (response.error) throw new Error(response.error.message);
      const data = response.data;
      if (!data?.success) throw new Error(data?.error || "Failed to create gift");

      // Open the new gift page in a new tab and close dialog
      toast({ title: "🎉 Подарочная страница готова!", description: "Открыли её в новой вкладке." });
      if (data.gift_url) {
        window.open(data.gift_url, "_blank", "noopener,noreferrer");
      }
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: "Ошибка", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Gift className="w-5 h-5 text-primary" />
            Подарочная страница
          </DialogTitle>
          <DialogDescription>
            Персональная страница с плеером, анимацией и посланием
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Live preview */}
          <GiftPreviewMockup
            heroName={heroName}
            customMessage={customMessage}
            senderName={senderName}
            eventName={eventName}
            style={giftStyle}
          />
          <a
            href={giftStyle === "black" ? "/g/юра-fv4pc6" : "/g/татьяна-6cieqj"}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-xs text-center text-primary hover:underline"
          >
            Посмотреть пример →
          </a>

          {/* Hero name */}
          <div className="space-y-1.5">
            <Label htmlFor="hero_name">Имя получателя *</Label>
            <Input
              id="hero_name"
              placeholder="Например: Мария"
              value={heroName}
              onChange={(e) => setHeroName(e.target.value)}
              maxLength={50}
            />
          </div>

          {/* Event */}
          <div className="space-y-1.5">
            <Label>Событие *</Label>
            <div className="flex flex-wrap gap-1.5">
              {EVENT_PRESETS.map((preset) => (
                <button
                  key={preset}
                  onClick={() => setEventName(preset)}
                  className={`text-xs px-2.5 py-1.5 rounded-full border transition-all ${
                    eventName === preset
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-primary/50"
                  }`}
                >
                  {preset}
                </button>
              ))}
            </div>
            <Input
              placeholder="Или введите своё"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              maxLength={50}
            />
          </div>

          {/* Sender name */}
          <div className="space-y-1.5">
            <Label htmlFor="sender_name">Ваше имя (опционально)</Label>
            <Input
              id="sender_name"
              placeholder="Будет показано на странице"
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              maxLength={50}
            />
          </div>

          {/* Custom message */}
          <div className="space-y-1.5">
            <Label htmlFor="custom_message">Личное сообщение (опционально)</Label>
            <Textarea
              id="custom_message"
              placeholder="Напишите пожелание..."
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              maxLength={500}
              rows={3}
            />
          </div>

          {/* Track version */}
          {audioUrl2 && (
            <div className="space-y-1.5">
              <Label>Версия трека</Label>
              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedVersion(1)}
                  className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-all ${
                    selectedVersion === 1
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-primary/50"
                  }`}
                >
                  Версия 1
                </button>
                <button
                  onClick={() => setSelectedVersion(2)}
                  className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-all ${
                    selectedVersion === 2
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-primary/50"
                  }`}
                >
                  Версия 2
                </button>
              </div>
            </div>
          )}

          {/* Style */}
          <div className="space-y-1.5">
            <Label>Стиль страницы</Label>
            <div className="flex gap-2">
              <button
                onClick={() => setGiftStyle("light")}
                className={`flex-1 py-3 rounded-lg border text-sm font-medium transition-all ${
                  giftStyle === "light"
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:border-primary/50"
                }`}
              >
                ☀️ Светлый
              </button>
              <button
                onClick={() => setGiftStyle("black")}
                className={`flex-1 py-3 rounded-lg border text-sm font-medium transition-all ${
                  giftStyle === "black"
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:border-primary/50"
                }`}
              >
                🌙 Тёмный
              </button>
            </div>
          </div>

          {/* CTA */}
          <Button
            onClick={handleCreate}
            disabled={loading || !heroName.trim() || !eventName.trim()}
            className="w-full h-12 text-base font-bold gap-2"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Sparkles className="w-5 h-5" />
            )}
            Создать бесплатно
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            ✨ Эффект открытия • 🎨 Премиальный дизайн • 💌 Личное сообщение • 🔗 Ссылка для отправки
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
